/**
 * Invoices > Bill flat contracts (tracker item 34).
 *
 * Every FLAT_CONTRACT recurring schedule's current billing period, with its
 * status (due, already billed), plus any missed earlier periods that are
 * still due. The office selects the periods to bill (current due periods are
 * selected by default; missed ones must be ticked) and confirms. Each
 * period becomes its own posted invoice dated today. Shown in both billing
 * modes: in AUTOMATIC mode it shows what the server billed and handles
 * catch-up. Every amount comes from the server; the only client math is the
 * total of the selected rows.
 *
 * Route-gated to ADMIN / OFFICE_MANAGER, like the backend
 * requireRole('ADMIN', 'OFFICE_MANAGER') on /api/flat-contract-billing.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { createAPIClient, IFlatContractBillingApi } from '../api';
import * as types from '../api/types';
import { primaryButtonClass, secondaryButtonClass } from '../components/formStyles';
import { invoiceDate, invoiceMoney, invoiceStatus } from './invoiceUi';
import { COMPANY_SETTINGS_PATH, toggleSelection } from './financeChargeUi';
import { recurringDetailPath } from './recurringUi';
import {
  MODE_LABELS,
  billConfirmMessage,
  billableKeys,
  defaultSelection,
  flatContractError,
  flatContractStatusLabel,
  keyToItem,
  modeDescription,
  periodLabel,
  rowKey,
  selectedTotal,
  toggleAllRows,
} from './flatContractBillingUi';
import { Toast } from '../components/Toast';

const cell = (numeric = false) => `px-4 py-3 ${numeric ? 'text-right tabular-nums whitespace-nowrap' : 'text-left'}`;
const COLUMN_COUNT = 8;

function BillResult({ result }: { result: types.BillFlatContractsResult }) {
  const count = result.billed.length;
  return (
    <section aria-label="Billing result" className="mb-6 rounded-lg bg-white p-5 shadow">
      <h2 className="text-lg font-semibold text-gray-900">Billing result</h2>
      {count === 0 ? (
        <><Toast kind="info">Nothing was billed. No invoices were created.</Toast><p className="mt-2 text-gray-700">Nothing was billed. No invoices were created.</p></>
      ) : (
        <>
          <Toast kind="success">Created {count} flat contract {count === 1 ? 'invoice' : 'invoices'} totaling {invoiceMoney(result.totalBilled)}, dated {invoiceDate(result.invoiceDate)}.</Toast>
          <p className="mt-2 text-green-800">
            Created {count} flat contract {count === 1 ? 'invoice' : 'invoices'} totaling {invoiceMoney(result.totalBilled)}, dated {invoiceDate(result.invoiceDate)}.
          </p>
        </>
      )}
      {count > 0 && (
        <ul className="mt-3 space-y-1 text-sm">
          {result.billed.map((entry) => (
            <li key={entry.invoiceId}>
              <Link to={`/invoices/${entry.invoiceId}`} className="font-semibold text-blue-700 hover:underline">#{entry.invoiceNumber}</Link>
              {' '}{entry.billingCustomerName}: {invoiceMoney(entry.total)}
              <span className="text-gray-500"> for {periodLabel(entry)}</span>
            </li>
          ))}
        </ul>
      )}
      {result.skipped.length > 0 && (
        <div className="mt-4">
          <h3 className="text-sm font-semibold text-gray-900">Not billed ({result.skipped.length})</h3>
          <ul className="mt-1 space-y-1 text-sm">
            {result.skipped.map((entry) => (
              <li key={`${entry.scheduleId}-${entry.periodStartDate}`} className={entry.reason === 'ERROR' ? 'text-red-700' : 'text-gray-700'}>
                {entry.customerName ?? entry.scheduleId} ({invoiceDate(entry.periodStartDate)}): {flatContractStatusLabel(entry.reason)}
                {entry.message && <span className="text-gray-500"> ({entry.message})</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

export function FlatContractBillingPage() {
  const api = useMemo<IFlatContractBillingApi>(() => createAPIClient(), []);
  const [preview, setPreview] = useState<types.FlatContractPreview | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [billing, setBilling] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<types.BillFlatContractsResult | null>(null);
  const requestSeq = useRef(0);

  const loadPreview = useCallback(async (options: { keepResult?: boolean } = {}) => {
    const seq = ++requestSeq.current;
    setLoading(true);
    setError(null);
    if (!options.keepResult) setResult(null);
    try {
      const next = await api.previewFlatContractBilling();
      if (seq !== requestSeq.current) return;
      setPreview(next);
      setSelected(defaultSelection(next.rows));
    } catch (cause) {
      if (seq !== requestSeq.current) return;
      setPreview(null);
      setSelected([]);
      setError(flatContractError(cause, 'Could not load the flat contract preview'));
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    void loadPreview();
  }, [loadPreview]);

  const rows = preview?.rows ?? [];
  const keys = billableKeys(rows);
  const selectedKeys = selected.filter((key) => keys.includes(key));
  const total = selectedTotal(rows, selectedKeys);
  const allSelected = keys.length > 0 && selectedKeys.length === keys.length;
  const missedSelected = rows.filter((row) => !row.isCurrent && selectedKeys.includes(rowKey(row))).length;
  const busy = loading || billing;

  async function handleBill() {
    if (!preview || selectedKeys.length === 0 || billing) return;
    if (!window.confirm(billConfirmMessage(selectedKeys.length, total, missedSelected))) return;
    setBilling(true);
    setError(null);
    setResult(null);
    try {
      // Only the periods the user reviewed
      const outcome = await api.billFlatContracts({ items: selectedKeys.map(keyToItem) });
      setResult(outcome);
      await loadPreview({ keepResult: true });
    } catch (cause) {
      setError(flatContractError(cause, 'Could not bill the flat contracts'));
    } finally {
      setBilling(false);
    }
  }

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <Link to="/invoices" className="text-sm text-blue-700 hover:underline">← Invoices</Link>
      <h1 className="text-2xl font-bold text-gray-900 mt-2">Bill flat contracts</h1>
      <p className="mt-1 mb-4 max-w-3xl text-sm text-gray-600">
        Recurring schedules billed as a flat contract get one invoice per billing period for the contract amount plus tax, issued at the
        start of the period however many visits there are. Each period can be billed once; cancelling its invoice does not make it
        billable again.
      </p>

      {preview && (
        <div role="note" className="mb-6 max-w-3xl rounded-md border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
          <p><strong>Billing mode: {MODE_LABELS[preview.mode] ?? preview.mode}.</strong> {modeDescription(preview.mode)}</p>
          <Link to={COMPANY_SETTINGS_PATH} className="mt-1 inline-block font-semibold underline">Company settings</Link>
        </div>
      )}

      {error && <Toast kind="error">{error}</Toast>}
      {result && <BillResult result={result} />}

      {loading && !preview && <p className="text-gray-600">Loading preview…</p>}

      {preview && (
        <section className="rounded-lg bg-white p-5 shadow">
          <div className="mb-4 flex flex-wrap items-baseline justify-between gap-2">
            <h2 className="text-lg font-semibold text-gray-900">Periods as of {invoiceDate(preview.asOfDate)}</h2>
            <div className="flex w-full flex-wrap gap-2 sm:w-auto">
              {/* The select-all checkbox lives in the table header, which is hidden on phones */}
              {keys.length > 0 && (
                <button type="button" className={`${secondaryButtonClass} min-h-[44px] flex-1 md:hidden`} disabled={busy} onClick={() => setSelected(toggleAllRows(rows, selectedKeys))}>
                  {allSelected ? 'Clear selection' : 'Select all due'}
                </button>
              )}
              <button type="button" className={`${secondaryButtonClass} min-h-[44px] flex-1 sm:flex-none md:min-h-0`} disabled={busy} onClick={() => void loadPreview()}>
                {loading ? 'Loading…' : 'Refresh'}
              </button>
            </div>
          </div>
          {preview.truncatedScheduleIds.length > 0 && (
            <p className="mb-3 text-sm text-amber-800">
              Some schedules have more missed periods than are listed (the most recent are shown). Bill those first, then refresh.
            </p>
          )}

          <div className="overflow-x-auto">
            <table className="responsive-table min-w-full divide-y divide-gray-200 text-sm">
              <caption className="sr-only">Flat contract billing periods</caption>
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-4 py-3 w-10">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      disabled={busy || keys.length === 0}
                      onChange={() => setSelected(toggleAllRows(rows, selectedKeys))}
                      aria-label="Select all due periods"
                      className="h-4 w-4 rounded border-gray-300"
                    />
                  </th>
                  {[
                    { label: 'Bill to' },
                    { label: 'Property' },
                    { label: 'Period' },
                    { label: 'Amount', numeric: true },
                    { label: 'Tax', numeric: true },
                    { label: 'Total', numeric: true },
                    { label: 'Status' },
                  ].map((column) => (
                    <th key={column.label} scope="col" className={`px-4 py-3 font-medium text-gray-600 ${column.numeric ? 'text-right' : 'text-left'}`}>
                      {column.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={COLUMN_COUNT} className="px-4 py-10 text-center text-gray-600">
                      No active flat contract has a period to bill. Set a recurring schedule's billing to Flat contract on its page.
                    </td>
                  </tr>
                ) : rows.map((row) => {
                  const key = rowKey(row);
                  return (
                    <tr key={key} className={row.billable ? undefined : 'text-gray-500'}>
                      <td className="px-4 py-3 w-10">
                        {row.billable && (
                          <input
                            type="checkbox"
                            checked={selectedKeys.includes(key)}
                            disabled={busy}
                            onChange={() => setSelected((current) => toggleSelection(current, key))}
                            aria-label={`Bill ${row.billingCustomerName} for ${periodLabel(row)}`}
                            className="h-5 w-5 md:h-4 md:w-4 rounded border-gray-300"
                          />
                        )}
                      </td>
                      <td data-label="Bill to" data-primary className={cell()}>
                        <Link to={`/customers/${row.billingCustomerId}`} className="font-semibold text-blue-700 hover:underline">{row.billingCustomerName}</Link>
                      </td>
                      <td data-label="Property" className={cell()}>
                        <Link to={recurringDetailPath(row.scheduleId)} className="text-blue-700 hover:underline">{row.propertyAddress}</Link>
                      </td>
                      <td data-label="Period" className={`${cell()} md:whitespace-nowrap`}>
                        <div>
                          {periodLabel(row)}
                          {!row.isCurrent && <span className="ml-2 whitespace-nowrap rounded bg-amber-50 px-1.5 py-0.5 text-xs text-amber-800 ring-1 ring-amber-200">Missed</span>}
                        </div>
                      </td>
                      <td data-label="Amount" className={cell(true)}>{invoiceMoney(row.amount)}</td>
                      <td data-label="Tax" className={cell(true)}>{invoiceMoney(row.tax)}</td>
                      <td data-label="Total" className={`${cell(true)} font-semibold`}>{invoiceMoney(row.total)}</td>
                      <td data-label="Status" className={cell()}>
                        <div>
                          <span className="font-medium">{flatContractStatusLabel(row.status)}</span>
                          {row.invoice && (
                            <span className="block text-xs">
                              <Link to={`/invoices/${row.invoice.invoiceId}`} className="text-blue-700 hover:underline">#{row.invoice.invoiceNumber}</Link>
                              {' '}{invoiceStatus(row.invoice.status)}
                            </span>
                          )}
                          {!row.invoice && row.note && <span className="block text-xs">{row.note}</span>}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="mt-5 flex flex-col gap-3 border-t pt-4 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-gray-700" aria-live="polite">
              {keys.length === 0
                ? 'Nothing is due to bill.'
                : <>Selected <strong>{selectedKeys.length}</strong> of {keys.length} due · Total <strong>{invoiceMoney(total)}</strong></>}
            </p>
            <button type="button" disabled={busy || selectedKeys.length === 0} onClick={() => void handleBill()} className={`${primaryButtonClass} min-h-[44px] w-full sm:w-auto md:min-h-0`}>
              {billing ? 'Billing…' : 'Bill selected'}
            </button>
          </div>
        </section>
      )}
    </main>
  );
}
